import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ClipboardCopy } from "lucide-react";
import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useSchemaBasedProvider } from "../providers/useSchemaBasedProvider";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import { ROUTES } from "@/constants/routes";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import { useIntegrationModuleClientDetailsBreadcrumbs } from "@/features/integrations/hooks/useIntegrationModuleClientDetailsBreadcrumbs";
import {
  getActionHandlers,
  renderComponent,
} from "@/features/integrations/shared/renderer";
import { decodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import useToast from "@/lib/hooks/useToast";

interface ClientModuleDetailPageProps {
  /** Module name on the backend (e.g. "job-sync", "organic"). */
  moduleName: string;
  /** Translation sub-key namespace (e.g. "jobSync", "organic"). */
  moduleSubKey: string;
  /** Render the schema-derived title and subtitle on the page. */
  showTitle?: boolean;
  /** Render a custom header above the schema UI (e.g. Indeed sync overview header). */
  renderHeader?: (params: {
    clientId: string;
    integrationName: string;
  }) => ReactNode;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const detail = (error.response?.data as { detail?: string } | undefined)
      ?.detail;
    return detail || error.message || String(error);
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

export function ClientModuleDetailPage({
  moduleName,
  moduleSubKey,
  showTitle = false,
  renderHeader,
}: ClientModuleDetailPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { clientId, integrationName, moduleName: moduleNameParam } = useParams<{
    clientId?: string;
    integrationName?: string;
    moduleName?: string;
  }>();
  const { tenantId } = useAuth();
  const { searchValue, setSearchValue } = useSearchSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler();

  const { breadcrumbs, integration } =
    useIntegrationModuleClientDetailsBreadcrumbs(moduleName);

  const decodedIntegrationName = integrationName
    ? decodeSlug(integrationName)
    : null;
  const backendIntegrationName = decodedIntegrationName ?? "";

  const effectiveModuleName = moduleNameParam || moduleName;

  const moduleQuery = useIntegrationModule({
    integrationName: backendIntegrationName,
    moduleName: effectiveModuleName,
    tenantId: tenantId ?? undefined,
    clientId: clientId ?? undefined,
  });

  useEffect(() => {
    if (moduleQuery.isPending || !integrationName) return;

    const shouldRedirect =
      moduleQuery.isError ||
      moduleQuery.data === null ||
      (moduleQuery.data !== undefined && !moduleQuery.data.isActive);

    if (!shouldRedirect) return;

    const redirectPath = clientId
      ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`
      : `/${ROUTES.integrations}/${integrationName}`;
    navigate(redirectPath, { replace: true });
  }, [
    moduleQuery.isPending,
    moduleQuery.isError,
    moduleQuery.data,
    integrationName,
    clientId,
    navigate,
  ]);

  const schema = useSchemaBasedProvider({
    integrationName: backendIntegrationName,
    moduleName,
    moduleSubKey,
    clientId,
  });

  const ui = useMemo(() => schema.getClientViews(), [schema]);

  const actionHandlers = useMemo(
    () => getActionHandlers(backendIntegrationName),
    [backendIntegrationName],
  );

  const executeAction = useCallback(
    async (
      actionKey: string,
      messages: { successMessage: string; errorMessage: string },
    ) => {
      const handler = actionHandlers[actionKey];
      if (!handler) return;
      try {
        await handler({
          integrationName: backendIntegrationName,
          moduleName: effectiveModuleName,
          tenantId: tenantId ?? undefined,
          clientId: clientId ?? undefined,
          queryClient: {
            invalidateQueries: (options) =>
              queryClient.invalidateQueries(
                options as Parameters<typeof queryClient.invalidateQueries>[0],
              ),
            resetQueries: (options) =>
              queryClient.resetQueries(
                options as Parameters<typeof queryClient.resetQueries>[0],
              ),
            refetchQueries: async (options) => {
              await queryClient.refetchQueries(
                options as Parameters<typeof queryClient.refetchQueries>[0],
              );
            },
            getQueryData: <T = unknown, >(queryKey: readonly unknown[]) =>
              queryClient.getQueryData<T>(queryKey),
            setQueryData: <T = unknown, >(
              queryKey: readonly unknown[],
              data: T | undefined,
            ) => {
              queryClient.setQueryData<T>(queryKey, data);
            },
            removeQueries: (options) =>
              queryClient.removeQueries(
                options as Parameters<typeof queryClient.removeQueries>[0],
              ),
            cancelQueries: async (options) => {
              await queryClient.cancelQueries(
                options as Parameters<typeof queryClient.cancelQueries>[0],
              );
            },
          },
          onSuccess: () => {
            if (messages.successMessage) {
              showToast(t(messages.successMessage), "success");
            }
          },
          onError: (error) => {
            showToast(extractErrorMessage(error), "error");
          },
        });
      } catch (error) {
        showToast(extractErrorMessage(error), "error");
      }
    },
    [
      actionHandlers,
      backendIntegrationName,
      effectiveModuleName,
      tenantId,
      clientId,
      queryClient,
      showToast,
      t,
    ],
  );

  const handleSortWrapper = useCallback(
    (accessor: string | undefined, direction: string | null) => {
      if (!accessor) return;
      handleSort(accessor, direction as "asc" | "desc" | null);
    },
    [handleSort],
  );

  const context = useMemo(
    () => ({
      integrationName: backendIntegrationName,
      moduleName: effectiveModuleName,
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      executeAction,
      onSort: handleSortWrapper,
      orderBy: orderBy ?? null,
      descending: descending ?? false,
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      emptyMessage: t("common.noResultsFound"),
    }),
    [
      backendIntegrationName,
      effectiveModuleName,
      tenantId,
      clientId,
      executeAction,
      handleSortWrapper,
      orderBy,
      descending,
      searchValue,
      setSearchValue,
      t,
    ],
  );

  if (moduleQuery.isPending) return null;
  if (moduleQuery.isError || !moduleQuery.data?.isActive) return null;

  const translatedIntegrationName = integration?.displayName
    ? t(integration.displayName)
    : integration?.name || backendIntegrationName || "";

  const backHref =
    clientId && integrationName
      ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`
      : integrationName
        ? `/${ROUTES.integrations}/${integrationName}`
        : undefined;

  const title = showTitle
    ? t(schema.getClientDetailPageTitleKey(), {
      integrationName: translatedIntegrationName,
    })
    : undefined;
  const subtitle = showTitle
    ? t(schema.getClientDetailPageSubtitleKey(), { defaultValue: "" })
    : undefined;

  const debugCode = `MODULE_NOT_CONFIGURED | integration=${backendIntegrationName} module=${effectiveModuleName} client=${clientId}`;

  return (
    <ModernPageLayout
      backbutton={{ label: t("common.back"), href: backHref }}
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={subtitle}
    >
      {ui.length > 0 ? (
        <div className="space-y-4">
          {renderHeader && clientId
            ? renderHeader({ clientId, integrationName: backendIntegrationName })
            : null}
          {ui.map((component, index) => (
            <div key={`${component.component}-${index}`} className="space-y-4">
              {renderComponent(component, context)}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">
            {t("integrations.modules.notConfigured")}
          </p>
          <code className="rounded-lg bg-muted px-3 py-1.5 text-xs text-muted-foreground">
            {debugCode}
          </code>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(debugCode);
              showToast(
                t("common.copiedToClipboard"),
                "success",
              );
            }}
          >
            <ClipboardCopy className="mr-2 size-3.5" />
            {t("common.copyToClipboard")}
          </Button>
        </div>
      )}
    </ModernPageLayout>
  );
}
